import { configureStore, ThunkAction, Action, PreloadedState } from "@reduxjs/toolkit";
import { persistStore } from "redux-persist";
import persistedReducer from "./persistConfig";
import rootReducer from "./rootReducer";
import logger from "redux-logger";

/**
 * Creates and configures the main Redux store for the application.
 * - Uses `persistedReducer` to enable state persistence.
 * - Enables Redux DevTools in development mode.
 * - Adds `redux-logger` middleware in development mode for logging actions and state changes.
 * - Disables `serializableCheck` in middleware to allow non-serializable data in the state.
 *
 * @returns {EnhancedStore} The configured Redux store.
 */
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) => {
    const middlewares = getDefaultMiddleware({
      serializableCheck: false,
    });

    // Add logger middleware only in development mode, uncomment the following condition to enable it.
    // if (ENV_VARIABLES.NODE_ENV === "development") {
    //   middlewares.push(logger as Middleware);
    // }

    return middlewares;
  },
});

/**
 * Dynamically creates a Redux store instance.
 *
 * - Used for scenarios like testing or server-side rendering (SSR).
 * - Allows passing a `preloadedState` to initialize the store with specific data.
 * - Uses `rootReducer` instead of `persistedReducer` for a fresh store instance.
 *
 * @param {PreloadedState<RootState>} [preloadedState] - The initial state to preload into the store.
 * @returns {EnhancedStore} A new Redux store instance.
 */
export const setupStore = (preloadedState?: PreloadedState<RootState>) => {
  return configureStore({
    reducer: rootReducer,
    preloadedState,
    // In tests we allow non-serializable values like Date objects to avoid noisy warnings
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
        immutableCheck: false,
      }),
  });
};

/**
 * Creates a persistor instance for the Redux store.
 *
 * - Used to persist and rehydrate the Redux store state across sessions.
 *
 * @returns {Persistor} The persistor instance for the Redux store.
 */
export const persistor = persistStore(store);

/**
 * Type definitions for the Redux store and related utilities.
 *
 * - `AppStore`: The type of the Redux store created by `setupStore`.
 * - `AppDispatch`: The type of the `dispatch` function from the Redux store.
 * - `RootState`: The type of the root state object from the Redux store.
 * - `AppThunk`: A utility type for defining thunk actions.
 */
export type AppStore = ReturnType<typeof setupStore>;
export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action<string>>;
